import { useCallback, useEffect, useState } from 'react';
import { Loader } from '@mantine/core';
import { IconCheck, IconMail, IconX } from '@tabler/icons-react';
import { useAuthService } from '@actschurch/ccms-sdk';
import { useEnrichedAuth } from '@/hooks/useEnrichedAuth';
import { useCopy } from '@/lib/settings';
import {
  isEmailConfirmedLocally,
  markEmailConfirmedLocally,
} from '@/utils/emailConfirmationMarker';

export interface EmailConfirmationState {
  /** True when the banner should show (signed in, email not confirmed, not dismissed). */
  visible: boolean;
  email: string;
  busy: boolean;
  sentOk: boolean;
  error: string | null;
  resend: () => Promise<void>;
  dismiss: () => void;
}

/**
 * Whether the signed-in member still has to confirm their email, plus the resend action.
 * The shell uses it to show only one banner at a time (this one wins over the beta banner).
 */
export function useEmailConfirmation(): EmailConfirmationState {
  const { user, isAuthenticated } = useEnrichedAuth();
  const authService = useAuthService();

  const [dismissed, setDismissed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sentOk, setSentOk] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const email = (user as { email?: string | null } | null)?.email ?? '';
  // Cast through unknown so TS doesn't fight the (intentionally) loose user shape.
  const flag = (user as { emailConfirmed?: boolean } | null)?.emailConfirmed;

  // Watch the local "confirmed" marker so the banner disappears the moment the member opens the
  // confirmation link in another tab (custom event: same tab; storage event: other tabs).
  const [locallyConfirmed, setLocallyConfirmed] = useState(() => isEmailConfirmedLocally(email));
  useEffect(() => {
    setLocallyConfirmed(isEmailConfirmedLocally(email));
    const refresh = () => setLocallyConfirmed(isEmailConfirmedLocally(email));
    window.addEventListener('storage', refresh);
    window.addEventListener('ccms:email-confirmed', refresh as EventListener);
    return () => {
      window.removeEventListener('storage', refresh);
      window.removeEventListener('ccms:email-confirmed', refresh as EventListener);
    };
  }, [email]);

  // Authoritative check against the backend: on mount, on focus and every 30 s (the cached JWT
  // was minted before confirmation, the backend flag is the truth).
  useEffect(() => {
    if (!isAuthenticated || !email || flag !== false || locallyConfirmed) return;
    let cancelled = false;
    const check = async () => {
      try {
        const ok = await authService.getEmailConfirmationStatus(email);
        if (cancelled) return;
        if (ok) {
          markEmailConfirmedLocally(email); // also fans out to other tabs
          setLocallyConfirmed(true);
        }
      } catch {
        /* network blip: try again on the next tick */
      }
    };
    check();
    const onFocus = () => check();
    window.addEventListener('focus', onFocus);
    const timer = window.setInterval(check, 30_000);
    return () => {
      cancelled = true;
      window.removeEventListener('focus', onFocus);
      window.clearInterval(timer);
    };
  }, [isAuthenticated, email, flag, locallyConfirmed, authService]);

  const resend = useCallback(async () => {
    if (!email) return;
    setBusy(true);
    setError(null);
    try {
      await authService.resendConfirmation({ email, appUrl: window.location.origin });
      setSentOk(true);
      window.setTimeout(() => setSentOk(false), 6000);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'send_failed');
    } finally {
      setBusy(false);
    }
  }, [email, authService]);

  // Missing flag = older session: keep the banner hidden rather than falsely accusing everyone.
  const needsConfirmation = isAuthenticated && flag === false && !locallyConfirmed;
  return {
    visible: needsConfirmation && !dismissed,
    email,
    busy,
    sentOk,
    error,
    resend,
    dismiss: () => setDismissed(true),
  };
}

/**
 * Slim, light banner for signed-in members whose email isn't confirmed yet, with a text button
 * to send the link again (POST /api/auth/resend-confirmation). Dismissing hides it until the
 * next sign-in. Self-contained version; the shell uses useEmailConfirmation() + EmailConfirmationNotice
 * so it can show only one banner at a time.
 */
export function EmailConfirmationBanner() {
  const state = useEmailConfirmation();
  return <EmailConfirmationNotice state={state} />;
}

/** The banner itself, driven by useEmailConfirmation(). */
export function EmailConfirmationNotice({ state: s }: { state: EmailConfirmationState }) {
  const { t } = useCopy();
  if (!s.visible) return null;

  const message = s.sentOk
    ? t('shell.email_banner_sent', undefined, { email: s.email })
    : s.error
      ? t('shell.email_banner_error')
      : t('shell.email_banner', undefined, { email: s.email });

  return (
    <div className={`rides-banner ${s.sentOk ? 'ok' : 'info'}`} role="status" aria-live="polite">
      <div className="rides-banner-inner">
        <span className="ic" aria-hidden="true">{s.sentOk ? <IconCheck size={20} /> : <IconMail size={20} />}</span>
        <span className="msg">{message}</span>
        <span className="actions">
          {!s.sentOk && (
            <button type="button" className="rides-textbtn" onClick={s.resend} disabled={s.busy || !s.email}>
              {s.busy && <Loader size={14} aria-hidden="true" />}
              {s.busy ? 'Sending…' : s.error ? 'Try again' : 'Send it again'}
            </button>
          )}
          <button type="button" className="rides-iconbtn" onClick={s.dismiss} aria-label="Hide this message">
            <IconX size={18} aria-hidden="true" />
          </button>
        </span>
      </div>
    </div>
  );
}
